import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2, X } from 'lucide-react';
import type { DiningVenue, MenuItem } from '@natsepa/database';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';

// Helper function to generate slug from title
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function DiningList() {
  const { data, isLoading, error } = useList<DiningVenue>({ resource: 'dining' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this dining venue?')) {
      deleteOne({ resource: 'dining', id });
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading dining venues: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dining Venues</h1>
        <button
          onClick={() => create('dining')}
          className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Venue
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cuisine
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((venue) => (
              <tr key={venue.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{venue.name}</div>
                  <div className="text-sm text-gray-500">{venue.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {venue.cuisine || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {venue.openingHours || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => edit('dining', venue.id)}
                      className="p-2 text-gray-400 hover:text-gold-600 transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(venue.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data?.data.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No dining venues found. Click "Add Venue" to create one.
          </div>
        )}
      </div>
    </div>
  );
}

// Create Dining
export function DiningCreate() {
  const navigate = useNavigate();
  const { mutate: createDining, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    cuisine: '',
    openingHours: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.nameId) {
      alert('Please fill in the name');
      return;
    }

    const slug = form.slug || generateSlug(form.nameId);

    createDining(
      {
        resource: 'dining',
        values: { ...form, slug, name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn },
      },
      {
        onSuccess: (data) => {
          const newId = data?.data?.id;
          if (newId) {
            navigate(`/dining/edit/${newId}`);
          } else {
            navigate('/dining');
          }
        },
      }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/dining')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Dining Venue</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new restaurant or dining area</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {/* Basic Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

          <BilingualField
            label="Name"
            valueEn={form.nameEn}
            valueId={form.nameId}
            onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder={form.nameId ? generateSlug(form.nameId) : 'auto-generated'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Venue Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Venue Details</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cuisine Type</label>
              <input
                type="text"
                value={form.cuisine}
                onChange={(e) => setForm({ ...form, cuisine: e.target.value })}
                placeholder="e.g., Indonesian, International"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Opening Hours</label>
              <input
                type="text"
                value={form.openingHours}
                onChange={(e) => setForm({ ...form, openingHours: e.target.value })}
                placeholder="e.g., 07:00 - 22:00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Note about images */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> You can add images after creating the venue.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/dining')}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50"
          >
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Dining
export function DiningEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<DiningVenue>({ resource: 'dining', id: id! });
  const { mutate: updateDining, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    cuisine: '',
    openingHours: '',
  });
  const [images, setImages] = useState<ManagedImage[]>([]);
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [newMenuItem, setNewMenuItem] = useState({ name: '', description: '', price: '', category: '' });

  useEffect(() => {
    if (data?.data) {
      const venue = data.data;
      setForm({
        slug: venue.slug || '',
        nameEn: (venue as any).nameEn || (venue as any).name_en || venue.name || '',
        nameId: (venue as any).nameId || (venue as any).name_id || venue.name || '',
        descriptionEn: (venue as any).descriptionEn || (venue as any).description_en || venue.description || '',
        descriptionId: (venue as any).descriptionId || (venue as any).description_id || venue.description || '',
        shortDescriptionEn: (venue as any).shortDescriptionEn || (venue as any).short_description_en || venue.shortDescription || '',
        shortDescriptionId: (venue as any).shortDescriptionId || (venue as any).short_description_id || venue.shortDescription || '',
        cuisine: venue.cuisine || '',
        openingHours: venue.openingHours || '',
      });
      if (venue.images) {
        setImages(
          venue.images.map((url, index) => ({
            url,
            imageType: 'gallery' as const,
            sortOrder: index,
          }))
        );
      }
      if (venue.menu) {
        setMenu(venue.menu);
      }
    }
  }, [data]);

  const addMenuItem = () => {
    if (!newMenuItem.name) return;
    setMenu([...menu, {
      name: newMenuItem.name,
      nameEn: newMenuItem.name,
      nameId: newMenuItem.name,
      description: newMenuItem.description || undefined,
      descriptionEn: newMenuItem.description || undefined,
      descriptionId: newMenuItem.description || undefined,
      price: newMenuItem.price ? parseInt(newMenuItem.price) : undefined,
      category: newMenuItem.category || 'Main',
    }]);
    setNewMenuItem({ name: '', description: '', price: '', category: '' });
  };

  const removeMenuItem = (index: number) => {
    setMenu(menu.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateDining(
      {
        resource: 'dining',
        id: id!,
        values: {
          ...form,
          images: images.map((img) => img.url),
          menu: menu.length > 0 ? menu : undefined,
        },
      },
      {
        onSuccess: () => {
          navigate('/dining');
        },
      }
    );
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/dining')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Dining Venue</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Venue details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Form (2 columns) */}
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

            <BilingualField
              label="Name"
              valueEn={form.nameEn}
              valueId={form.nameId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })}
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50"
                readOnly
              />
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Description</h2>

            <BilingualField
              label="Short Description"
              valueEn={form.shortDescriptionEn}
              valueId={form.shortDescriptionId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'shortDescriptionEn' : 'shortDescriptionId']: value })}
              multiline
              rows={2}
            />

            <BilingualField
              label="Full Description"
              valueEn={form.descriptionEn}
              valueId={form.descriptionId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })}
              multiline
              rows={4}
            />
          </div>

          {/* Venue Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Venue Details</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cuisine Type</label>
                <input
                  type="text"
                  value={form.cuisine}
                  onChange={(e) => setForm({ ...form, cuisine: e.target.value })}
                  placeholder="e.g., Indonesian, International"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opening Hours</label>
                <input
                  type="text"
                  value={form.openingHours}
                  onChange={(e) => setForm({ ...form, openingHours: e.target.value })}
                  placeholder="e.g., 07:00 - 22:00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Menu Items</h2>

            {menu.length > 0 && (
              <div className="space-y-2">
                {menu.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      {item.description && (
                        <div className="text-sm text-gray-500">{item.description}</div>
                      )}
                      <div className="flex gap-4 mt-1 text-xs text-gray-400">
                        {item.category && <span>Category: {item.category}</span>}
                        {item.price && <span>Price: Rp {item.price.toLocaleString()}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMenuItem(index)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Add Menu Item</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    value={newMenuItem.name}
                    onChange={(e) => setNewMenuItem({ ...newMenuItem, name: e.target.value })}
                    placeholder="Item name *"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={newMenuItem.category}
                    onChange={(e) => setNewMenuItem({ ...newMenuItem, category: e.target.value })}
                    placeholder="Category (e.g., Main, Appetizer)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={newMenuItem.description}
                    onChange={(e) => setNewMenuItem({ ...newMenuItem, description: e.target.value })}
                    placeholder="Description (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={newMenuItem.price}
                    onChange={(e) => setNewMenuItem({ ...newMenuItem, price: e.target.value })}
                    placeholder="Price in Rupiah"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={addMenuItem}
                disabled={!newMenuItem.name}
                className="mt-3 flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/dining')}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50"
            >
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Changes
            </button>
          </div>
        </form>

        {/* Right: Images (1 column) */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Venue Images</h2>
            <ImageManager
              entityType="dining"
              entityId={id!}
              images={images}
              imageType="gallery"
              multiple={true}
              maxImages={10}
              onChange={setImages}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
